import { PointCloudObject } from "@/object-cache";
import { selectClippingBox } from "@/store/clipping-box-selectors";
import { setIpxRegionMeasures } from "@/store/modes/ipx-order-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { estimatePlanArea } from "@/tools/floor-area/estimate-plan-area";
import { estimateRegionPointCount } from "@/tools/point-sampling/estimate-region-point-count";
import {
  filterPointsInRegion,
  regionBounds,
} from "@/tools/point-sampling/filter-points-in-region";
import { sampleLodPoints } from "@/tools/point-sampling/sample-lod-points";
import { GUID } from "@faro-lotv/foundation";
import { useEffect, useRef } from "react";
import { Vector3Tuple } from "three";
import { useFloorAreaStore } from "./floor-area-store";

/** Same budget as the benchmark samples, a few seconds to download, enough to find walls at 20 cm */
export const MAX_SAMPLE_POINTS = 4_000_000;
/** The sampled region is larger than the box, so enlarging the box a bit doesn't need new points */
const SAMPLED_REGION_SCALE = 1.25;
/** Lets the panel show the progress before the estimation blocks the main thread */
const ESTIMATION_DELAY_MS = 50;

type UseFloorAreaEstimationProps = {
  /** The point cloud of the selected dataset */
  pointCloud: PointCloudObject;
  /** The scans left out of the selection */
  hiddenScans: GUID[];
  /** True while the measures are needed */
  isActive: boolean;
};

/** Measures the plan area and the points of the region box, or of the whole selection without a box, while active */
export function useFloorAreaEstimation({
  pointCloud,
  hiddenScans,
  isActive,
}: UseFloorAreaEstimationProps): void {
  const box = useAppSelector(selectClippingBox);
  const dispatch = useAppDispatch();
  const sample = useFloorAreaStore((state) => state.sample);
  const isSampling = useFloorAreaStore((state) => state.isSampling);
  const selectionKey = `${pointCloud.iElement.id}:${hiddenScans.join(",")}`;
  const running = useRef<AbortController>(undefined);
  const isCovered =
    sample?.selectionKey === selectionKey &&
    !!box &&
    sample.bounds.containsBox(regionBounds(box));

  useEffect(
    () => () => {
      running.current?.abort();
      useFloorAreaStore.setState({
        isSampling: false,
        sample: undefined,
        estimate: undefined,
      });
    },
    [],
  );

  useEffect(() => {
    if (isActive) return;
    running.current?.abort();
    useFloorAreaStore.setState({ isSampling: false });
  }, [isActive]);

  // Any change of what is sent makes the measures stale
  useEffect(() => {
    useFloorAreaStore.setState({
      estimate: undefined,
      error: undefined,
      hoveredFloor: undefined,
      pinnedFloor: undefined,
    });
    dispatch(setIpxRegionMeasures(undefined));
  }, [box, dispatch, selectionKey]);

  useEffect(() => {
    if (!box || !isActive || isSampling || isCovered) return;
    const region = {
      ...box,
      size: [
        box.size[0] * SAMPLED_REGION_SCALE,
        box.size[1] * SAMPLED_REGION_SCALE,
        box.size[2] * SAMPLED_REGION_SCALE,
      ] satisfies Vector3Tuple,
    };
    const abort = new AbortController();
    running.current = abort;
    useFloorAreaStore.setState({
      isSampling: true,
      sampledPoints: 0,
      error: undefined,
    });
    sampleLodPoints(pointCloud, {
      region,
      maxPoints: MAX_SAMPLE_POINTS,
      hiddenScanIndices: new Set(
        hiddenScans.flatMap((id) => pointCloud.scanIdsToIndex?.[id] ?? []),
      ),
      signal: abort.signal,
      onProgress: (sampledPoints) =>
        useFloorAreaStore.setState({ sampledPoints }),
    })
      .then(({ positions, origin, levelEnds }) =>
        useFloorAreaStore.setState({
          isSampling: false,
          sample: {
            positions,
            origin,
            levelEnds,
            bounds: regionBounds(region),
            selectionKey,
          },
        }),
      )
      .catch((error: unknown) => {
        if (abort.signal.aborted) return;
        useFloorAreaStore.setState({
          isSampling: false,
          error: error instanceof Error ? error.message : String(error),
        });
      });
  }, [
    box,
    hiddenScans,
    isActive,
    isCovered,
    isSampling,
    pointCloud,
    selectionKey,
  ]);

  useEffect(() => {
    if (
      !box ||
      !isActive ||
      !isCovered ||
      useFloorAreaStore.getState().estimate
    ) {
      return;
    }
    const timeout = setTimeout(() => {
      const { positions, origin } = sample;
      const estimate = estimatePlanArea(
        filterPointsInRegion(positions, origin, box),
      );
      useFloorAreaStore.setState({ estimate: { ...estimate, origin } });

      const count = estimateRegionPointCount(pointCloud, sample, box);
      dispatch(
        setIpxRegionMeasures({
          planAreaM2: estimate.totalAreaM2,
          export: count && {
            points: count.points,
            bytes: count.points * count.bytesPerPoint,
          },
        }),
      );
    }, ESTIMATION_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [box, dispatch, isActive, isCovered, pointCloud, sample]);
}
